import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import localforage from 'localforage';
import { flatten, noop, pickBy, uniq } from 'lodash';

import useFetchClaimFlagConfigurations from '~/components/ClaimFlags/hooks/useFetchClaimFlagConfigurations';
import useFetchClaimFieldsConfigurations from '~/components/ClaimPage/hooks/useFetchClaimFieldsConfiguration';
import useFetchClaimStageConfigurations from '~/components/ClaimStages/hooks/useFetchClaimStageConfigurations';
import useCachedDataFetcher from '~/components/hooks/useCachedDataFetcher';
import { useCms } from '~/components/hooks/useCms';
import LoadingIndicator from '~/components/LoadingIndicator';
import { reportAxiosError } from '~/Utils';

const OrganizationContext = React.createContext({});

function OrganizationContextProvider(props) {
  const { children } = props;
  const { user } = useCms();
  const [showLoading, setShowLoading] = useState(false);
  const organizationId = props.organizationId || user.organization_id;

  useEffect(() => {
    // Copies cached under the pre-v2 key hold credentials the response no longer carries.
    localforage.removeItem(`operational_details_${organizationId}`);
  }, [organizationId]);

  const {
    isLoading: isLoadingOperationalDetails,
    isError: isErrorOperationalDetails,
    data: operationalDetailsData,
    reloadData: reloadOperationalDetails,
  } = useCachedDataFetcher({
    url: `/api/v1/organizations/${organizationId}/operational_details`,
    shouldUseCache: true,
    localCacheKey: `operational_details_v2_${organizationId}`,
    shouldFetch: !props.operationalDetailsData,
  });

  const {
    isLoading: isLoadingContactRoles,
    isError: isErrorContactRoles,
    data: organizationContactRolesDict,
    reloadData: reloadContactRoles,
  } = useCachedDataFetcher({
    url: `/api/v1/contact_roles/organizations/${organizationId}/roles`,
    shouldUseCache: true,
    localCacheKey: `contact_roles_${organizationId}`,
    shouldFetch: !props.organizationContactRolesDict,
  });

  const {
    isLoading: isLoadingNotificationCategories,
    isError: isErrorNotificationCategories,
    data: notificationCategories,
    reloadData: reloadNotificationCategories,
  } = useCachedDataFetcher({
    url: `/api/v1/organizations/${organizationId}/categories`,
    shouldUseCache: true,
    localCacheKey: `notification_categories_${organizationId}`,
    shouldFetch: !props.notificationCategories,
  });

  const {
    isLoading: isLoadingCommunicationCategories,
    isError: isErrorCommunicationCategories,
    data: communicationCategories,
    reloadData: reloadCommunicationCategories,
  } = useCachedDataFetcher({
    url: `/api/v1/organizations/${organizationId}/communication_categories`,
    shouldUseCache: true,
    localCacheKey: `communication_categories_${organizationId}`,
    shouldFetch: !props.communicationCategories,
  });

  const {
    isLoading: isLoadingCommunicationConfiguration,
    isError: isErrorCommunicationConfiguration,
    data: communicationConfiguration,
    reloadData: reloadCommunicationConfiguration,
  } = useCachedDataFetcher({
    url: `/api/v1/organizations/${organizationId}/communication_configuration`,
    shouldUseCache: true,
    localCacheKey: `communication_configuration_${organizationId}`,
    shouldFetch: !props.communicationConfiguration,
  });

  const {
    fieldsConfigurations: claimFieldsConfigurations,
    isLoading: isLoadingClaimFieldsConfigurations,
    isError: isErrorClaimFieldsConfigurations,
    reloadData: reloadClaimFieldsConfigurations,
  } = useFetchClaimFieldsConfigurations({
    organizationId,
    shouldFetch: !props.claimFieldsConfigurations,
  });

  const {
    claimStageConfigurations,
    isLoading: isLoadingClaimStageConfigurations,
    isError: isErrorClaimStageConfigurations,
    reloadData: reloadClaimStageConfigurations,
  } = useFetchClaimStageConfigurations({
    organizationId,
    shouldFetch: !props.claimStageConfigurations, // i.e. in testing setup
  });

  const {
    claimFlagConfigurations,
    isLoading: isLoadingClaimFlagConfigurations,
    isError: isErrorClaimFlagConfigurations,
    reloadData: reloadClaimFlagConfigurations,
  } = useFetchClaimFlagConfigurations({
    organizationId,
    shouldFetch: !props.claimFlagConfigurations, // i.e. in testing setup
  });

  // Refresh every org-scoped config fetcher in one call. A Config Promotion apply can mutate config that
  // lives here (contact roles, notification/communication categories, communication config, Claim
  // Bar/Sidebar & stage config) rather than on the org model, so the apply flow calls this alongside the
  // org-model reload. allSettled so one failing fetcher can't abort the rest; each reloadData is a silent,
  // cache-correct GET (it never flips isLoading), so this is a background refresh with no loading flash.
  const reloadOrganizationContext = useCallback(
    () =>
      Promise.allSettled([
        reloadOperationalDetails(),
        reloadContactRoles(),
        reloadNotificationCategories(),
        reloadCommunicationCategories(),
        reloadCommunicationConfiguration(),
        reloadClaimFieldsConfigurations(),
        reloadClaimStageConfigurations(),
        reloadClaimFlagConfigurations(),
      ]),
    [
      reloadOperationalDetails,
      reloadContactRoles,
      reloadNotificationCategories,
      reloadCommunicationCategories,
      reloadCommunicationConfiguration,
      reloadClaimFieldsConfigurations,
      reloadClaimStageConfigurations,
      reloadClaimFlagConfigurations,
    ]
  );

  const resolvedOperationalDetails = operationalDetailsData || props.operationalDetailsData;
  const isLoading = isLoadingOperationalDetails || isLoadingContactRoles || isLoadingClaimFieldsConfigurations;
  // A finished, non-errored fetch that still yielded no operational details (e.g. an empty/undefined
  // response body) cannot build the context — surface it as an error rather than destructuring undefined.
  const isMissingOperationalDetails =
    !isLoadingOperationalDetails && !isErrorOperationalDetails && !resolvedOperationalDetails;
  const isError = isErrorOperationalDetails || isErrorContactRoles || isMissingOperationalDetails;

  useEffect(() => {
    // Set timeout to delay the loading component
    const timer = setTimeout(() => {
      if (isLoading) {
        setShowLoading(true);
      }
    }, 1000);
    // Cleanup timeout on component unmount
    return () => clearTimeout(timer);
  }, [isLoading]);

  let contextValues = { isLoading, isError };
  if (!isLoading && !isError) {
    contextValues = {
      ...contextValues,
      organizationId,
      ...prepareOperationalDetails(resolvedOperationalDetails),
      organizationContactRolesDict: organizationContactRolesDict || props.organizationContactRolesDict,
      notificationCategories: notificationCategories || props.notificationCategories,
      communicationCategories: communicationCategories || props.communicationCategories,
      communicationConfiguration: communicationConfiguration || props.communicationConfiguration,
      reloadOrganizationContext,
      reloadOperationalDetails,
      reloadContactRoles,
      reloadNotificationCategories,
      reloadCommunicationCategories,
      reloadCommunicationConfiguration,
      isLoadingNotificationCategories,
      isErrorNotificationCategories,
      isLoadingCommunicationCategories,
      isErrorCommunicationCategories,
      isLoadingCommunicationConfiguration,
      isErrorCommunicationConfiguration,
      claimFieldsConfigurations,
      reloadClaimFieldsConfigurations,
      isErrorClaimFieldsConfigurations,
      claimStageConfigurations,
      reloadClaimStageConfigurations,
      isLoadingClaimStageConfigurations,
      isErrorClaimStageConfigurations,
      claimFlagConfigurations,
      reloadClaimFlagConfigurations,
      isLoadingClaimFlagConfigurations,
      isErrorClaimFlagConfigurations,
      fetchLobs: (sub_orgs_ids) => {
        try {
          const route = `/api/v1/organizations/${organizationId}/lobs`;
          return axios.get(route, {
            params: { sub_orgs_ids },
          });
        } catch (error) {
          return reportAxiosError(error);
        }
      },
    };
  }

  if (isLoading) {
    return showLoading ? <LoadingIndicator isError={isError} /> : null;
  }

  return <OrganizationContext.Provider value={contextValues}>{children}</OrganizationContext.Provider>;
}

OrganizationContextProvider.propTypes = {
  children: PropTypes.node,
  organizationId: PropTypes.number,
  operationalDetailsData: PropTypes.object,
  organizationContactRolesDict: PropTypes.object,
  notificationCategories: PropTypes.array,
  communicationCategories: PropTypes.array,
  communicationConfiguration: PropTypes.object,
  claimFieldsConfigurations: PropTypes.object,
  claimStageConfigurations: PropTypes.array,
  claimFlagConfigurations: PropTypes.array,
};

function prepareOperationalDetails(apiRetData) {
  // Defensive: callers gate on loaded-and-present data, but never destructure undefined (a crash that took
  // down the whole authenticated app when a poisoned cache replayed an empty payload).
  if (!apiRetData) {
    return {};
  }
  const {
    google_places_api_key: googlePlacesApiKey,
    mor_configuration: morConfiguration,
    is_managed_repair_enabled,
    legacy_expense_payment_types: legacyExpensePaymentTypes,
    legacy_indemnity_payment_types: legacyIndemnityPaymentTypes,
    outgoing_physical_mail_method: outgoingPhysicalMailMethod,
    policy_search_config: policySearchConfig,
    supported_claim_types: supportedClaimTypes,
    supported_coverages: supportedCoverages,
    incident_types_dict: incidentTypesDict,
    injury_types_dict: injuryTypesDict,
    body_parts_dict: bodyPartsDict,
    is_body_parts_list: isBodyPartList,
    gl_source_codes_dict: glSourceCodesDict,
    gl_cause_codes_dict: glCauseCodesDict,
    insured_properties_and_locations: insuredPropertiesAndLocationsDict,
    home_causes_of_loss_dict: homeCausesOfLossDict,
    reopen_exposure_options: reopenExposureOptions,
    reopen_claim_options: reopenClaimOptions,
    incident_details_extra_fields: incidentDetailsExtraFields,
    exposures_closing_reasons_dict: exposuresClosingReasonsDict,
    document_types_dict: documentTypesDict,
    org_additional_configs: orgAdditionalConfigs,
    moi_options: moiOptions,
    fnol_submission_types: fnolSubmissionTypes,
    org_required_fields_for_close_claim: orgRequiredFieldsForCloseClaim,
    sub_reserves: subReserves,
    moi_options_order: moiOptionsOrder,
    litigation_status_dict: litigationStatusDict,
    eob_adjustment_reasons: eobAdjustmentReasons,
    sub_organizations_enabled: subOrganizationEnabled,
    sub_organizations: subOrganizations,
    is_twilio_enabled: isTwilioEnabled,
    is_dyte_video_initialized: isDyteVideoInitialized,
    is_dyte_video_enabled: isDyteVideoEnabled,
    gbg_validation_status: gbgValidationStatus,
    is_metabase_initialized: isMetabaseInitialized,
    is_metabase_enabled: isMetabaseEnabled,
    is_metabase_reporting_configured: isMetabaseReportingConfigured,
    vehicle_types_dict: vehicleTypesDict,
    close_claim_reasons: closeClaimReasons,
    is_general_email_queue_enabled: isGeneralEmailQueueEnabled,
    is_catastrophe_events_enabled: isCatastropheEventsEnabled,
    is_performance_qa_enabled: isPerformanceQaEnabled,
    org_tableau_configs: orgTableauConfigs,
    org_moi_expertises: organizationMoiExpertises,
    is_policy_api_enabled: isPolicyAPIEnabled,
    notifications_tokens: notificationsTokens,
    notification_usable_tokens_map: notificationUsableTokensMap,
    coverage_configuration: coverageConfiguration,
    is_navigator_enabled: isNavigatorEnabled,
    is_claim_search_enabled: isClaimSearchEnabled,
    is_xactanalysis_enabled: isXactAnalysisEnabled,
    is_notes_and_notifications_enabled: isNotesAndNotificationsEnabled,
    org_level_supported_lobs: orgLevelSupportedLobs,
    claim_numbering_scheme: claimNumberingScheme,
    lob_configuration_dict: lobConfigurationsDict,
    twilio_configuration: twilioConfiguration,
    control_file_options: controlFileOptions,
    email_domains: emailDomains,
    is_license_correlation_enabled: isLicenseCorrelationEnabled,
    is_license_presentation_enabled: isLicensePresentationEnabled,
    tableau_accounts_configuration: tableauAccountsConfiguration,
    moi_configuration: moiConfiguration,
    is_ccc_enabled: isCccEnabled,
    payments_configuration: paymentsConfiguration,
    physical_mail_types_configuration: physicalMailTypesConfiguration,
    is_multiple_countries_enabled: isMultipleCountriesEnabled,
    is_ai_enabled: isAiEnabled,
    is_delegated_authority_enabled: isDelegatedAuthorityEnabled,
    is_fnol_inbox_configured: isFnolInboxConfigured,
    is_e_sign_enabled: isESignEnabled,
  } = apiRetData;

  return {
    googlePlacesApiKey,
    morConfiguration,
    managedRepairStatus: { is_managed_repair_enabled },
    outgoingPhysicalMailMethod,
    policySearchConfig,
    supportedClaimTypes,
    supportedCoverages,
    incidentTypesDict,
    injuryTypesDict,
    bodyPartsDict,
    isBodyPartList,
    glSourceCodesDict,
    glCauseCodesDict,
    reopenExposureOptions,
    reopenClaimOptions,
    insuredPropertiesAndLocationsDict,
    homeCausesOfLossDict,
    legacyExpensePaymentTypes,
    legacyIndemnityPaymentTypes,
    incidentDetailsExtraFields,
    exposuresClosingReasonsDict,
    documentTypesDict,
    orgAdditionalConfigs,
    moiOptions,
    fnolSubmissionTypes,
    orgRequiredFieldsForCloseClaim,
    subReserves,
    moiOptionsOrder,
    litigationStatusDict,
    eobAdjustmentReasons,
    subOrganizationEnabled,
    subOrganizations,
    isTwilioEnabled,
    isDyteVideoInitialized,
    isDyteVideoEnabled,
    gbgValidationStatus,
    isMetabaseInitialized,
    isMetabaseEnabled,
    isMetabaseReportingConfigured,
    vehicleTypesDict,
    closeClaimReasons,
    isGeneralEmailQueueEnabled,
    isCatastropheEventsEnabled,
    orgTableauConfigs,
    organizationMoiExpertises,
    isPolicyAPIEnabled,
    notificationsTokens,
    notificationUsableTokensMap,
    isPerformanceQaEnabled,
    coverageConfiguration,
    isNavigatorEnabled,
    isClaimSearchEnabled,
    isXactAnalysisEnabled,
    isNotesAndNotificationsEnabled,
    orgLevelSupportedLobs,
    claimNumberingScheme,
    lobConfigurationsDict,
    twilioConfiguration,
    controlFileOptions,
    emailDomains,
    isLicenseCorrelationEnabled,
    isLicensePresentationEnabled,
    tableauAccountsConfiguration,
    moiConfiguration,
    isCccEnabled,
    paymentsConfiguration,
    physicalMailTypesConfiguration,
    isMultipleCountriesEnabled,
    isAiEnabled,
    isDelegatedAuthorityEnabled,
    isFnolInboxConfigured,
    isESignEnabled,
  };
}

const withOrganization = (WrappedComponent) => {
  class withOrganization extends React.Component {
    render() {
      return (
        <OrganizationContext.Consumer>
          {(organizationContext) => <WrappedComponent {...organizationContext} {...this.props} />}
        </OrganizationContext.Consumer>
      );
    }
  }

  withOrganization.displayName = `withOrganization(${getDisplayName(WrappedComponent)})`;
  return withOrganization;
};

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

// Notice: possibleAdjustersWithDaSpecialist returns with removed users. remove them manually
function useOrganization() {
  return useContext(OrganizationContext);
}

function OrganizationContextUIOnlyProvider(props) {
  const { children, organizationValues, lobConfigurationsDict = {} } = props;

  const orgLevelSupportedLobs = organizationValues?.supported_lobs;
  const envType = organizationValues?.organization?.organization_type || 'operational';

  const fetchLobs = (sub_orgs_ids) => {
    let lobsToSend = [];
    if (organizationValues?.organization?.sub_organizations_enabled === false) {
      lobsToSend = orgLevelSupportedLobs;
    } else {
      const selectedSubOrgs =
        sub_orgs_ids && sub_orgs_ids.length
          ? organizationValues?.sub_organizations.filter(({ external_id }) => sub_orgs_ids.includes(external_id))
          : organizationValues?.sub_organizations;

      lobsToSend = uniq(flatten(selectedSubOrgs?.map(({ supported_lobs }) => supported_lobs.map(({ lob }) => lob))));
    }
    return { data: lobsToSend || [] };
  };

  const prepareCoverages = (coverages) => {
    return coverages?.map((coverage) => ({
      ...coverage,
      sub_organizations: coverage.sub_organization_ids.map((externalId) => {
        return organizationValues?.sub_organizations.find((subOrg) => subOrg.external_id === externalId);
      }),
    }));
  };

  const prepareSharedCoverages = (sharedCoverages, coverages) => {
    return prepareCoverages(sharedCoverages).map((sc) => ({
      ...sc,
      coverage_config_display_names: sc.coverage_config_ids
        .map((key) => {
          return coverages.find((c) => c.coverage_key === key)?.display_name;
        })
        .filter(Boolean),
    }));
  };

  const contextValues = {
    isLoading: false,
    isError: false,
    ...prepareOperationalDetails({
      ...organizationValues,
      supported_claim_types: fetchLobs(),
      org_level_supported_lobs: orgLevelSupportedLobs,
      lob_configuration_dict:
        envType === 'demo'
          ? lobConfigurationsDict
          : pickBy(lobConfigurationsDict, (value, _) => value?.env_type === 'production'),
    }),
    organizationContactRolesDict: {},
    notificationCategories: [],
    communicationCategories: [],
    subOrganizations: organizationValues?.sub_organizations?.map((subOrg) => ({ ...subOrg, id: subOrg.external_id })),
    coverages: prepareCoverages(organizationValues?.coverages),
    sharedCoverages: prepareSharedCoverages(organizationValues?.shared_coverages, organizationValues?.coverages),
    reloadOperationalDetails: noop,
    fetchLobs,
  };

  if (contextValues.organization) {
    contextValues.organization.sub_organizations = contextValues?.sub_organizations;
  }

  return <OrganizationContext.Provider value={contextValues}>{children}</OrganizationContext.Provider>;
}

OrganizationContextUIOnlyProvider.propTypes = {
  children: PropTypes.node.isRequired,
  organizationValues: PropTypes.object,
  lobConfigurationsDict: PropTypes.object,
};

export default useOrganization;
export {
  OrganizationContext,
  OrganizationContextProvider,
  OrganizationContextUIOnlyProvider,
  prepareOperationalDetails,
  withOrganization,
};
